import { endOfDay, endOfWeek, format, parseISO, startOfDay, startOfWeek, subHours } from 'date-fns';

export type DatePreset = 'last12h' | 'last24h' | 'thisWeek' | null;
export type DateFilterBounds = [number | null, number | null];

export type DateRangeValue = {
  preset: DatePreset;
  range: DateFilterBounds;
};

export const presets: { value: DatePreset; label: string; getRange: () => DateFilterBounds }[] = [
  {
    value: 'last12h',
    label: 'Last 12H',
    getRange: (): DateFilterBounds => {
      const now = new Date();
      const end = endOfDay(now);
      const start = subHours(now, 12);
      return [start.getTime(), end.getTime()];
    },
  },
  {
    value: 'last24h',
    label: 'Last 24H',
    getRange: (): DateFilterBounds => {
      const now = new Date();
      const end = endOfDay(now);
      const start = subHours(now, 24);

      return [start.getTime(), end.getTime()];
    },
  },
  {
    value: 'thisWeek',
    label: 'This week',
    getRange: (): DateFilterBounds => {
      const now = new Date();
      const todayEnd = endOfDay(now).getTime();
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 }).getTime();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 }).getTime();

      // Cap at end of today so the range never extends past "now" (matches maxDate on the picker)
      return [weekStart, Math.min(weekEnd, todayEnd)];
    },
  },
];

export const toMantineDateValue = (timestamp: number | null): string | null => {
  if (!timestamp) {
    return null;
  }

  return format(new Date(timestamp), 'yyyy-MM-dd');
};

export const toMantineRangeValue = (range: DateFilterBounds): [string | null, string | null] => [
  toMantineDateValue(range[0]),
  toMantineDateValue(range[1]),
];

export const toDate = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }

  return parseISO(value);
};

export const toDateFilterBounds = (range: [string | null, string | null]): DateFilterBounds => {
  const startDate = toDate(range[0]);
  const endDate = toDate(range[1]);

  return [startDate ? startOfDay(startDate).getTime() : null, endDate ? endOfDay(endDate).getTime() : null];
};

// Month shown when the picker mounts; prefers the range end (most recent), then start, then today
export const getDefaultCalendarDate = (range: DateFilterBounds): Date => {
  const [start, end] = range;

  return new Date(end ?? start ?? Date.now());
};

export const normalizeDateRangeValueOnApply = (date: DateRangeValue): DateRangeValue => {
  if (date.preset != null) {
    return date;
  }

  const [start, end] = date.range;

  if (start != null && end == null) {
    return { preset: null, range: [start, endOfDay(new Date(start)).getTime()] };
  }
  if (start == null && end != null) {
    return { preset: null, range: [startOfDay(new Date(end)).getTime(), end] };
  }

  return date;
};
